import { useCallback, useEffect, useState } from 'react'
import { isCalmNpsRoute } from './npsCalmRoutes'

/** Mirrors mobile's NpsGate.sessionOnce: at most one prompt per browser session. */
export const NPS_SESSION_KEY = 'wehunt.nps.shownThisSession'

/**
 * Dwell before the card appears. Mobile waits 10s of foreground time; web waits
 * for the page to settle instead, and 6s is the figure the design prototype
 * shipped with.
 */
export const NPS_DWELL_MS = 6000

export interface NpsPromptTimingInput {
  eligible: boolean
  pathname: string
}

export interface NpsPromptTiming {
  shouldShow: boolean
  /** Call when the prompt has actually been shown, so it does not return. */
  markShown: () => void
}

// sessionStorage throws in some privacy configurations. A prompt is never worth
// an exception, so both accessors degrade to "not shown yet" / "do nothing".
function readShown(): boolean {
  try {
    return sessionStorage.getItem(NPS_SESSION_KEY) !== null
  } catch {
    return false
  }
}

function writeShown(): void {
  try {
    sessionStorage.setItem(NPS_SESSION_KEY, '1')
  } catch {
    // Ignored: worst case the prompt may reappear in a later session.
  }
}

/**
 * Decides WHEN it is polite to show the prompt. Knows nothing about who is
 * eligible — that verdict arrives as `eligible`.
 *
 * The dwell restarts on every navigation and on every return to the tab, so the
 * card only ever appears on a page the user has actually settled on.
 */
export function useNpsPromptTiming({ eligible, pathname }: NpsPromptTimingInput): NpsPromptTiming {
  const [armed, setArmed] = useState(false)
  const [shown, setShown] = useState(readShown)
  // Bumped to restart the dwell when the tab regains focus.
  const [visibilityEpoch, setVisibilityEpoch] = useState(0)

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setArmed(false)
        setVisibilityEpoch((n) => n + 1)
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  useEffect(() => {
    setArmed(false)
    if (shown || !eligible || !isCalmNpsRoute(pathname)) return
    if (document.visibilityState === 'hidden') return

    const timer = window.setTimeout(() => setArmed(true), NPS_DWELL_MS)
    return () => window.clearTimeout(timer)
  }, [eligible, pathname, shown, visibilityEpoch])

  const markShown = useCallback(() => {
    writeShown()
    setShown(true)
    setArmed(false)
  }, [])

  return { shouldShow: armed && !shown, markShown }
}
